import React from 'react';
import { Group, Line, Rect, Circle, Label, Tag, Text } from 'react-konva';
import type { PartyMember, NPC, RenderedImageRect, InteractionState } from '../../types/types.ts';
import type { Stage } from 'konva/lib/Stage';
import type Konva from 'konva';
import { useGridCalculator } from '../../hooks/useGridCalculator.ts';
import { useGrid } from '../GridProvider/useGrid.ts';
import { useGridService } from '../../hooks/useGridService';
import type { Hex } from '../../services/grid.service';
import { useRenderLoopDetection } from '../../hooks/hookUtils';

interface KonvaInteractionLayerProps {
  imageRect: RenderedImageRect | null;
  interaction: InteractionState | null;
  drawingMode: string | null;
  hoveredCell: { row: number; col: number } | null;
  tooltipState: { x: number; y: number; visible: boolean; cell: { row: number; col: number } | null };
  gridOrigin: { x: number; y: number };
  saveRegion: () => void;
  stageRef: React.RefObject<Stage | null>;
  selectedMarkerId: number | null | undefined;
  partyRoster: PartyMember[];
  npcs: NPC[];
}

const KonvaInteractionLayer: React.FC<KonvaInteractionLayerProps> = ({
  imageRect, interaction, drawingMode, hoveredCell, selectedMarkerId, partyRoster, npcs,
}) => {
  // Development-time render loop detection for this frequently-updating interaction layer
  useRenderLoopDetection('KonvaInteractionLayer');

  // Computation/context hooks (not Zustand store hooks) - allowed in presenter per ADR-034
  const { gridSettings, getCellRenderData } = useGridCalculator({ imageRect });
  const { gridStrategy } = useGrid();
  const gridService = useGridService(imageRect);


  const renderInteraction = () => {
    if (!interaction || !imageRect) return null;

    switch (interaction.type) {
      case 'line': {
        const points = (interaction.points ?? []).flatMap(p => [p.x, p.y]);
        return <Line points={points} stroke={drawingMode === 'journey' ? '#3399ff' : '#ff3333'} strokeWidth={2} dash={[5, 5]} tension={0.5} />;
      }
      case 'grid-path': {
        if (gridStrategy?.type !== 'hex' || !interaction.start || !interaction.current) return null;
        const startHex = gridService.pointToHex(interaction.start);
        const endHex = gridService.pointToHex(interaction.current);
        const hexesOnPath = gridService.getHexesBetween(startHex, endHex);
        const points = (hexesOnPath ?? []).flatMap((hex: Hex) => {
          const center = hex.center; // Correct: it's a getter
          return [center.x, center.y];
        });

        const distance = hexesOnPath.length > 0 ? hexesOnPath.length - 1 : 0;
        const distanceInUnits = distance * (gridSettings.distancePerUnit ?? 5);
        const unitName = gridSettings.distanceUnit ?? 'ft';

        return (
          <Group>
            <Line points={points} stroke="#39e639" strokeWidth={3} dash={[10, 5]} tension={0} />
            <Label x={interaction.current.x + 15} y={interaction.current.y + 15}>
              <Tag fill="rgba(0, 0, 0, 0.75)" pointerDirection="left" pointerWidth={10} pointerHeight={8} />
              <Text
                text={`${distanceInUnits} ${unitName} (${distance} hexes)`}
                fontFamily="monospace"
                fontSize={14}
                padding={6}
                fill="white"
              />
            </Label>
          </Group>
        );
      }
      case 'cone': {
        if (gridStrategy?.type !== 'hex' || !interaction.start || !interaction.current) return null;
        const startHex = gridService.pointToHex(interaction.start);
        const endHex = gridService.pointToHex(interaction.current);

        const angle = Math.atan2(endHex.y - startHex.y, endHex.x - startHex.x) * 180 / Math.PI;
        const direction = Math.round(angle / 60 + 6) % 6;

        const radius = gridService.distance(startHex, endHex);
        const hexesInCone = gridService.getCone({ start: startHex, direction, radius });

        return (hexesInCone ?? []).map((hex: Hex) => {
          const points = (hex.corners ?? []).map((p: { x: number, y: number }) => [p.x, p.y]).flat();
          return <Line
            key={`cone-${hex.q}-${hex.r}`}
            points={points}
            fill="rgba(255, 77, 79, 0.5)"
            stroke="rgba(255, 77, 79, 1)"
            strokeWidth={1}
            closed />;
        });
      }
      case 'shape': {
        const { start, current } = interaction;
        if (interaction.shape === 'rect') {
          return <Rect x={Math.min(start.x, current.x)} y={Math.min(start.y, current.y)} width={Math.abs(start.x - current.x)} height={Math.abs(start.y - current.y)} stroke="#ff9900" strokeWidth={2} dash={[5, 5]} />;
        } else { // circle
          const dx = start.x - current.x;
          const dy = start.y - current.y;
          return <Circle x={start.x} y={start.y} radius={Math.sqrt(dx * dx + dy * dy)} stroke="#ff9900" strokeWidth={2} dash={[5, 5]} />;
        }
      }
      case 'region': {
        if (!gridSettings?.enabled) return null;
        return Array.from(interaction.selectedCells).map(cellKey => {
          const parts = cellKey.split('-').map(Number);
          const row = parts[0]!;
          const col = parts[1]!;
          const cellData = getCellRenderData(row, col);
          if (!cellData) return null;

          if (gridSettings.type === 'square') {
            return <Rect key={cellKey} x={cellData.x} y={cellData.y} width={cellData.width} height={cellData.height} fill="rgba(0, 123, 255, 0.4)" />;
          } else if (gridSettings.type === 'hex' && cellData.points) {
            const points = cellData.points.split(' ').flatMap((p: string) => p.split(',').map(Number));
            return <Line key={cellKey} points={points} fill="rgba(0, 123, 255, 0.4)" closed />;
          }
          return null;
        });
      }
      default:
        return null;
    }
  };

  const renderHoverHighlighter = () => {
    if (!hoveredCell || !gridSettings?.enabled) return null;
    const cellData = getCellRenderData(hoveredCell.row, hoveredCell.col);
    if (!cellData) return null;

    if (gridSettings.type === 'square') {
      return <Rect x={cellData.x} y={cellData.y} width={cellData.width} height={cellData.height} fill="rgba(0, 123, 255, 0.3)" stroke="rgba(0, 123, 255, 0.8)" strokeWidth={1} />;
    } else if (gridSettings.type === 'hex' && cellData.points) {
      const points = cellData.points.split(' ').flatMap((p: string) => p.split(',').map(Number));
      return <Line points={points} fill="rgba(0, 123, 255, 0.3)" stroke="rgba(0, 123, 255, 0.8)" strokeWidth={1} closed />;
    }
    return null;
  };

  const renderRangeHighlighter = () => {
    if (!selectedMarkerId || gridStrategy?.type !== 'hex' || !imageRect) return null;

    // Find the selected marker's data
    const allMarkers = [
      ...partyRoster,
      ...npcs,
    ];
    const marker = (allMarkers ?? []).find(m => m.id === selectedMarkerId);
    if (!marker) return null;

    // Convert marker's percentage position to pixel coordinates
    const markerPixelPos = {
      x: imageRect.x + imageRect.width * ((Number(marker.posX) || 50) / 100),
      y: imageRect.y + imageRect.height * ((Number(marker.posY) || 50) / 100),
    };

    const centerHex = gridService.pointToHex(markerPixelPos);
    const range = 6; // Example: 30ft movement / 5ft per hex
    const hexesInRange = gridService.getHexesInRange(centerHex, range);

    return (hexesInRange ?? []).map((hex: Hex) => {
      const points = (hex.corners ?? []).map((p: { x: number, y: number }) => [p.x, p.y]).flat();
      return <Line key={`range-${hex.q}-${hex.r}`} points={points} fill="rgba(0, 150, 255, 0.25)" closed />;
    });
  };

  const clipFunc = (ctx: Konva.Context) => {
    if (imageRect) {
      ctx.beginPath();
      ctx.rect(imageRect.x, imageRect.y, imageRect.width, imageRect.height);
      ctx.closePath();
      ctx.clip();
    }
  };

  return (
    <Group listening={false} clipFunc={clipFunc}>
      {renderInteraction()}
      {renderHoverHighlighter()}
      {renderRangeHighlighter()}
      {/* Tooltip is rendered in KonvaMap, so it won't be clipped */}
    </Group>
  );
};

export default KonvaInteractionLayer;
